import { useEffect, useState } from 'react';
import {
  InvoiceIssuedCsvRowSchema,
  type AgingBucket,
  type InvoiceStatus,
  type PublicUser,
} from '@otter/shared';
import { InvoicePill } from '../components/Pill';
import { api, downloadFile } from '../lib/api';
import { parseCsv } from '../lib/csv';
import { fmt, fmtDate } from '../lib/format';

type Props = {
  user: PublicUser;
  scope: string;
  onOpen: (id: string | 'new') => void;
  onError: (e: unknown) => void;
};

type Row = {
  id: string;
  formattedNumber: string | null;
  status: InvoiceStatus;
  clientName: string;
  ownerCode: string;
  issueDate: string | null;
  dueDate: string | null;
  totalAmount: string;
  concept: string;
  paymentTermsDays: number;
  agingDays: number | null;
  agingBucket: AgingBucket | null;
  isPrivate: boolean;
};

function chipClass(b: AgingBucket): string {
  return b === 'al-dia' ? 'al-dia' : `b${b.replace('+', '')}`;
}

function agingLabel(days: number | null, bucket: AgingBucket | null): string {
  if (bucket === null || days === null) return '—';
  if (bucket === 'al-dia') return days === 0 ? 'hoy' : `+${-days} d.`;
  if (bucket === '90+') return '> 90 d.';
  return `${days} d.`;
}

const STATUSES: Array<{ id: 'all' | InvoiceStatus; label: string }> = [
  { id: 'all', label: 'todos' },
  { id: 'borrador', label: 'borrador' },
  { id: 'emitida', label: 'emitida' },
  { id: 'enviada', label: 'enviada' },
  { id: 'cobrada', label: 'cobrada' },
  { id: 'vencida', label: 'vencida' },
];

export function FacturasEmitidas({ user, scope, onOpen, onError }: Props) {
  const [items, setItems] = useState<Row[]>([]);
  const [status, setStatus] = useState<'all' | InvoiceStatus>('all');
  const [search, setSearch] = useState('');

  async function load() {
    try {
      const q: Record<string, string> = {};
      if (status !== 'all') q.status = status;
      if (search) q.search = search;
      if (scope !== 'all') {
        const owners = await api<{ items: Array<{ id: string; code: string }> }>('/admin/team');
        const f = owners.items.find((o) => o.code === scope);
        if (f) q.ownerId = f.id;
      }
      const res = await api<{ items: Row[] }>('/admin/invoices-issued', { query: q });
      setItems(res.items);
    } catch (e) {
      onError(e);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, scope, search]);

  return (
    <div className="body">
      <div className="pageh">
        <h1>Facturas emitidas</h1>
        <div className="sub">
          {items.length} factura{items.length === 1 ? '' : 's'} ·{' '}
          <span className="v">scope = {scope}</span>
        </div>
        <div className="actions">
          <input
            type="file"
            accept=".csv"
            id="csv-import-em"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const { valid, invalid } = await parseCsv(f, InvoiceIssuedCsvRowSchema);
                if (invalid.length > 0) {
                  console.warn('Invalid CSV rows', invalid);
                }
                if (valid.length === 0) return;
                const r = await api<{ created: number; errors: unknown[] }>(
                  '/admin/invoices-issued/import-csv',
                  { method: 'POST', body: { rows: valid } },
                );
                console.log(`Imported ${r.created} invoices`);
                void load();
              } catch (err) {
                onError(err);
              } finally {
                e.target.value = '';
              }
            }}
          />
          <button
            className="btn"
            onClick={() => downloadFile('/admin/invoices-issued/export-csv', 'invoices-issued.csv')}
          >
            Export CSV
          </button>
          <button className="btn" onClick={() => document.getElementById('csv-import-em')?.click()}>
            Import CSV
          </button>
          <button className="btn primary" onClick={() => onOpen('new')}>
            <span className="plus">+</span> Nueva factura
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="chips">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              className={`chip ${status === s.id ? 'on' : ''}`}
              onClick={() => setStatus(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="spacer" />
        <div className="search">
          <input
            placeholder="buscar número o concepto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="card">
        <table className="t">
          <thead>
            <tr>
              <th>Número</th>
              <th>Cliente</th>
              <th>Cond.</th>
              <th>Comercial</th>
              <th>Concepto</th>
              <th>Estado</th>
              <th>Aging</th>
              <th>Emisión</th>
              <th>Vencimiento</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} onClick={() => onOpen(r.id)}>
                <td className="mono" data-label="Número">
                  {r.formattedNumber ?? '— borrador —'}
                  {r.isPrivate && <span className="privada-tag">privada</span>}
                </td>
                <td data-label="Cliente">
                  <b>{r.clientName}</b>
                </td>
                <td className="mono" style={{ color: 'var(--muted)', fontSize: 11 }} data-label="Cond.">
                  Net {r.paymentTermsDays}
                </td>
                <td data-label="Comercial">
                  <span className="av-mini av-A">{r.ownerCode}</span>
                </td>
                <td style={{ color: 'var(--muted)' }} data-label="Concepto">{r.concept}</td>
                <td data-label="Estado">
                  <InvoicePill status={r.status} />
                </td>
                <td data-label="Aging">
                  {r.agingBucket ? (
                    <span className={`aging-chip ${chipClass(r.agingBucket)}`}>
                      {agingLabel(r.agingDays, r.agingBucket)}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>
                  )}
                </td>
                <td className="mono" data-label="Emisión">{fmtDate(r.issueDate)}</td>
                <td className="mono" data-label="Vencimiento">{fmtDate(r.dueDate)}</td>
                <td className="num" data-label="Total">{fmt(r.totalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty">sin facturas que mostrar</div>}
      </div>
    </div>
  );
}
